import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { setAuthSession } from '@/services/projectService';
import { getStoredCollege, clearStoredCollege } from '@/lib/collegeCapture';
// Learn-progress kept on the device (quiz commitments, prediction runs) is
// namespaced by the signed-in learner. Publish the scope from the SAME two
// places the user is set, so a sign-in as somebody else cannot be missed.
import { setLearnerScope, purgeLegacyLearnKeys } from '@/utils/learnerScope';
import { setLearner, track } from '@/services/learnerEvents';
import { getStoredAttribution, clearStoredAttribution } from '@/lib/siteAttribution';
import { deviceType } from '@/lib/deviceType';

// The learner session.signed_in was last recorded for (#570). Supabase
// re-announces SIGNED_IN on token refresh and tab return, so the signal is keyed
// on WHO is signed in: once per sign-in or app load, never per auth event.
let signalledFor = null;

const noteLearner = (session) => {
  const id = session?.user?.id ?? null;
  setLearner(id);
  // Which kind of device (#568): phone / tablet / desktop, from coarse signals only.
  if (id && signalledFor !== id) track('session.signed_in', null, { device: deviceType() });
  signalledFor = id;
};

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionRef = useRef(null);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      sessionRef.current = session;
      setAuthSession(session); // keep the API token cache in sync (paywall needs it)
      setSession(session);
      setLearnerScope(session?.user?.id ?? null);
      noteLearner(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // One-time: drop the pre-scoping keys, which were shared by everyone who
    // used this browser.
    purgeLegacyLearnKeys();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        sessionRef.current = session;
        setAuthSession(session); // keep the API token cache in sync
        setSession(session);
        setLearnerScope(session?.user?.id ?? null);
        noteLearner(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Sync user to database on sign in (use session from event, not getSession())
        if (event === 'SIGNED_IN' && session?.user) {
          syncUserToDatabase(session.user, session.access_token);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Sync user profile to our database
  const syncUserToDatabase = async (authUser, accessToken) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      // Use the passed access token instead of calling getSession() again
      const token = accessToken || sessionRef.current?.access_token;
      // The backend takes WHO is syncing from this token alone (go-live auth
      // fix); without one the call can only 401, so do not make it.
      if (!token) return;

      // Phase A — send the outreach-link college (if any) so the backend can
      // stamp users.college on first sync. Cleared once handed off.
      const college = getStoredCollege();
      // #568 — the public site's visitor id + first-touch UTM tags, if the
      // learner arrived through a site link. The backend keeps the first only.
      const attribution = getStoredAttribution();

      const res = await fetch(`${API_URL}/api/users/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        // id/email are ignored by the current backend (it reads them from the
        // token) but still sent so a backend deployed before it keeps working.
        body: JSON.stringify({
          id: authUser.id,
          email: authUser.email,
          display_name: authUser.user_metadata?.full_name || authUser.email?.split('@')[0],
          avatar_url: authUser.user_metadata?.avatar_url,
          ...(college ? { college } : {}),
          ...(attribution ? { attribution } : {})
        })
      });
      if (college) clearStoredCollege();
      // Kept until a sync gets through, then forgotten (the server has it).
      if (attribution && res.ok) clearStoredAttribution();
    } catch (error) {
      console.error('Failed to sync user:', error);
    }
  };

  // Sign in with email/password
  const signInWithEmail = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { data, error };
  };

  // Sign up with email/password
  const signUpWithEmail = async (email, password, displayName) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: displayName
          }
        }
      });
      
      // Check for rate limiting or other API errors
      if (error) {
        // Map common error codes to user-friendly messages
        if (error.message?.includes('rate limit') || error.status === 429) {
          return { 
            data: null, 
            error: { message: 'Too many signup attempts. Please wait a few minutes and try again.' }
          };
        }
        return { data, error };
      }
      
      return { data, error };
    } catch (err) {
      // Handle errors that occur during the request itself
      console.error('Signup request error:', err);
      
      // Check if the error message indicates rate limiting
      if (err.message?.includes('rate limit') || err.message?.includes('429')) {
        return { 
          data: null, 
          error: { message: 'Too many signup attempts. Please wait a few minutes and try again.' }
        };
      }
      
      return { 
        data: null, 
        error: { message: err.message || 'Failed to create account. Please try again.' }
      };
    }
  };

  // Sign in with Google
  // ADO #480 — redirectPath lets callers preserve a post-auth "next" target
  // (e.g. /start for the Try-for-Free flow). Defaults to /dashboard (ADO #331).
  // NOTE: the resolved `${origin}${redirectPath}` must be in Supabase Auth's
  // allowed Redirect URLs; add /start alongside /dashboard for each domain.
  const signInWithGoogle = async (redirectPath = '/dashboard') => {
    // Use window.location.origin to support any preview URL
    // This allows the OAuth to work regardless of which preview domain is being used
    const redirectUrl = window.location.origin;
    const safePath = (typeof redirectPath === 'string' && redirectPath.startsWith('/') && !redirectPath.startsWith('//'))
      ? redirectPath
      : '/dashboard';

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${redirectUrl}${safePath}`
      }
    });
    return { data, error };
  };

  // Send a password-reset email. The link lands on /reset-password, where the
  // user sets a new password (Supabase establishes a temporary recovery session
  // via the PASSWORD_RECOVERY event). The resolved redirect URL must be in
  // Supabase Auth's allowed Redirect URLs for each domain.
  const resetPassword = async (email) => {
    const redirectTo = `${window.location.origin}/reset-password`;
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    return { data, error };
  };

  // Set a new password for the currently-authenticated (or recovery) session.
  const updatePassword = async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    return { data, error };
  };

  // Sign out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const value = {
    user,
    session,
    loading,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    resetPassword,
    updatePassword,
    signOut,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
